import type { AuthBridge, AuthBridgeResponse } from 'owa-nested-app-auth-types';
import { getConfig } from 'owa-service/lib/config';
import getModuleContextMailboxInfo from 'owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo';
import { getOrigin } from 'owa-url/lib/getOrigin';
import { logStartUsage } from 'owa-analytics-start';

interface CustomGlobalThis {
    nestedAppAuthBridge?: AuthBridge;
}

declare var globalThis: CustomGlobalThis;

type NestedAppAuthBridgeCallback = (response: AuthBridgeResponse) => void;

/**
 * Call this function before initializing an MSAL PublicClientApplication to
 * set up the nested app auth bridge within the embedded application
 * @returns void
 */
export function createInlineNestedAppAuthBridge(targetWindow?: Window): void {
    const mailboxInfo = getModuleContextMailboxInfo();
    const isProjection = targetWindow !== undefined;
    const targetGlobalThis = (targetWindow ?? globalThis) as CustomGlobalThis & Window;

    if (!targetGlobalThis?.nestedAppAuthBridge) {
        return;
    }

    logStartUsage('InlineNAA_Bridge_Initialized', mailboxInfo, { isProjection });

    let registeredCallback: NestedAppAuthBridgeCallback | undefined;

    targetGlobalThis.nestedAppAuthBridge = {
        /**
         * Attach a callback to handle incoming NAA messages (NestedAppAuthResponse) from the host
         * @param eventName 'message'
         * @param callback callback to execute with NAA response string payload
         * As of now, we are only keeping one callback registered at a time for all partner apps,
         * so this will overwrite any previously registered callback but we plan to iterate on this
         * to support multiple callbacks in the future and that too per partner app
         */
        addEventListener: (eventName: string, callback: (response: AuthBridgeResponse) => void) => {
            logStartUsage('InlineNAA_Bridge_AddEventListener', mailboxInfo, {
                eventName,
                isProjection,
            });

            if (eventName === 'message' && callback) {
                registeredCallback = callback;
            }
        },

        /**
         * Send a NAA request payload to the inline host for execution
         * @param message NAA request string payload
         */
        postMessage: (message: string) => {
            logStartUsage('InlineNAA_Bridge_PostMessage', mailboxInfo, { isProjection });

            if (message) {
                const config = getConfig();
                if (!config?.executeNaaRequest) {
                    throw new Error('Nested app auth is unavailable');
                }

                config
                    .executeNaaRequest(
                        mailboxInfo,
                        message,
                        getOrigin(),
                        true,
                        targetGlobalThis,
                        'InlineNestedAppAuthBridge',
                        'PRIVATE'
                    )
                    .then((response: AuthBridgeResponse) => {
                        if (registeredCallback) {
                            registeredCallback(response);
                        }
                    });
            }
        },

        /**
         * Remove a previously added callback to handle incoming NAA messages (NestedAppAuthResponse) from the host
         * @param eventName 'message'
         * @param callback previously added callback to remove
         * Since we are only keeping one callback registered at a time and we remove it here,
         * so in order to set the callback, you will need to call `addEventListener` again.
         * We plan to iterate on this to support multiple callbacks in the future and that too per partner app.
         */
        removeEventListener: (
            eventName: string,
            callback: (response: AuthBridgeResponse) => void
        ) => {
            logStartUsage('InlineNAA_Bridge_RemoveEventListener', mailboxInfo, {
                eventName,
                isProjection,
            });
            if (eventName === 'message' && callback === registeredCallback) {
                registeredCallback = undefined;
            }
        },
    };
}
